/**
 * Frontend mirror of Atterberg pure-function calculations.
 *
 * MUST stay in sync with `backend/src/calculation/atterbergCalculations.ts`
 * and `backend/src/calculation/uscsClassification.ts`. Same formulas, same
 * rounding. Backend is the source of truth on save; this module powers live
 * preview as the user types.
 *
 * If the backend module changes, mirror the change here.
 */
import type {
  AtterbergGeneral,
  AtterbergDetail,
  AtterbergLLMethod,
  USCSGroupSymbol,
} from '../../../types/atterberg';

const SINGLE_POINT_EXPONENT = 0.121;
const REFERENCE_BLOWS = 25;

export interface TrialComputed {
  waterContent: number | null;
  massOfMoistSoil: number | null;
  massOfDrySoil: number | null;
  singlePointLL: number | null;
}

export interface LLRegression {
  slope: number;
  intercept: number;
  ll: number;
  /** Coefficient of determination of the WC-vs-log10(N) least-squares fit. */
  r2: number;
}

export interface AtterbergResult {
  ll: number | null;
  pl: number | null;
  pi: number | null;
  llRegression: LLRegression | null;
  /** FEAT-2026-0001: rounded LL from oven-dried retest trials. */
  retestLL: number | null;
  retestLLRegression: LLRegression | null;
}

/**
 * FEAT-2026-0001: 75% threshold per ASTM D2487 — if oven-dried retest LL is
 * less than 75% of the original LL, the soil is classified as organic.
 */
export const ORGANIC_RETEST_RATIO = 0.75;

export interface OrganicRetestStatus {
  status: 'organic' | 'not-organic' | 'incomplete';
  ratio: number | null;
  threshold: number | null;
}

export function evaluateOrganicByRetest(
  originalLL: number | null,
  retestLL: number | null,
): OrganicRetestStatus {
  if (originalLL === null || retestLL === null
      || Number.isNaN(originalLL) || Number.isNaN(retestLL)
      || originalLL <= 0) {
    return { status: 'incomplete', ratio: null, threshold: null };
  }
  const ratio = retestLL / originalLL;
  const threshold = ORGANIC_RETEST_RATIO * originalLL;
  return {
    status: ratio < ORGANIC_RETEST_RATIO ? 'organic' : 'not-organic',
    ratio,
    threshold,
  };
}

export function computeTrial(
  trial: Pick<AtterbergDetail, 'massOfContainer' | 'massOfWetSoilAndContainer' | 'massOfDrySoilAndContainer' | 'numberOfBlows'>,
  options?: { method?: AtterbergLLMethod },
): TrialComputed {
  const ctn = numOrNull(trial.massOfContainer);
  const wet = numOrNull(trial.massOfWetSoilAndContainer);
  const dry = numOrNull(trial.massOfDrySoilAndContainer);
  const n = numOrNull(trial.numberOfBlows);

  if (ctn === null || wet === null || dry === null) {
    return { waterContent: null, massOfMoistSoil: null, massOfDrySoil: null, singlePointLL: null };
  }

  const massOfMoistSoil = wet - ctn;
  const massOfDrySoil = dry - ctn;

  if (massOfDrySoil <= 0 || wet < dry) {
    return { waterContent: null, massOfMoistSoil, massOfDrySoil, singlePointLL: null };
  }

  const waterContent = ((massOfMoistSoil - massOfDrySoil) / massOfDrySoil) * 100;

  let singlePointLL: number | null = null;
  if (options?.method === 'SinglePoint' && n !== null && n > 0) {
    singlePointLL = waterContent * Math.pow(n / REFERENCE_BLOWS, SINGLE_POINT_EXPONENT);
  }

  return { waterContent, massOfMoistSoil, massOfDrySoil, singlePointLL };
}

export function computeLLMultipoint(trials: AtterbergDetail[]): LLRegression | null {
  const points: { x: number; y: number }[] = [];

  for (const t of trials) {
    const c = computeTrial(t);
    const n = numOrNull(t.numberOfBlows);
    if (c.waterContent === null || n === null || n <= 0) continue;
    points.push({ x: Math.log10(n), y: c.waterContent });
  }

  if (points.length < 2) return null;

  const n = points.length;
  const meanX = points.reduce((a, p) => a + p.x, 0) / n;
  const meanY = points.reduce((a, p) => a + p.y, 0) / n;

  let num = 0, den = 0;
  for (const p of points) {
    num += (p.x - meanX) * (p.y - meanY);
    den += (p.x - meanX) ** 2;
  }
  if (den === 0) return null;

  const slope = num / den;
  const intercept = meanY - slope * meanX;
  const llRaw = slope * Math.log10(REFERENCE_BLOWS) + intercept;

  // Coefficient of determination — R² = 1 − SS_res / SS_tot.
  // SS_tot = 0 means all WCs are equal (degenerate fit); report R² = 1 by
  // convention so callers can still display a value.
  let ssRes = 0, ssTot = 0;
  for (const p of points) {
    const yHat = slope * p.x + intercept;
    ssRes += (p.y - yHat) ** 2;
    ssTot += (p.y - meanY) ** 2;
  }
  const r2 = ssTot === 0 ? 1 : 1 - ssRes / ssTot;

  return { slope, intercept, ll: Math.round(llRaw), r2 };
}

export function computeLLSinglePoint(trials: AtterbergDetail[]): number | null {
  const lls: number[] = [];
  for (const t of trials) {
    const c = computeTrial(t, { method: 'SinglePoint' });
    if (c.singlePointLL !== null) lls.push(c.singlePointLL);
  }
  if (lls.length === 0) return null;
  const avg = lls.reduce((a, b) => a + b, 0) / lls.length;
  return Math.round(avg);
}

export function computePL(trials: AtterbergDetail[]): number | null {
  const wcs: number[] = [];
  for (const t of trials) {
    const c = computeTrial(t);
    if (c.waterContent !== null) wcs.push(c.waterContent);
  }
  if (wcs.length === 0) return null;
  const avg = wcs.reduce((a, b) => a + b, 0) / wcs.length;
  return Math.round(avg);
}

export function computePI(ll: number | null, pl: number | null): number | null {
  if (ll === null || pl === null) return null;
  return ll - pl;
}

export function computeAtterbergResult(input: {
  llTrials: AtterbergDetail[];
  plTrials: AtterbergDetail[];
  method: AtterbergLLMethod;
  isNonPlastic?: boolean;
  retestLLTrials?: AtterbergDetail[];
}): AtterbergResult {
  if (input.isNonPlastic) {
    return {
      ll: null, pl: null, pi: null, llRegression: null,
      retestLL: null, retestLLRegression: null,
    };
  }

  let ll: number | null;
  let llRegression: LLRegression | null = null;
  if (input.method === 'SinglePoint') {
    ll = computeLLSinglePoint(input.llTrials);
  } else {
    llRegression = computeLLMultipoint(input.llTrials);
    ll = llRegression?.ll ?? null;
  }

  let retestLL: number | null = null;
  let retestLLRegression: LLRegression | null = null;
  const retest = input.retestLLTrials ?? [];
  if (retest.length > 0) {
    if (input.method === 'SinglePoint') {
      retestLL = computeLLSinglePoint(retest);
    } else {
      retestLLRegression = computeLLMultipoint(retest);
      retestLL = retestLLRegression?.ll ?? null;
    }
  }

  const pl = computePL(input.plTrials);
  const pi = computePI(ll, pl);
  return { ll, pl, pi, llRegression, retestLL, retestLLRegression };
}

export function computePercentRetained425(
  totalMassOfSample: number | null | undefined,
  massRetained425um: number | null | undefined,
): number | null {
  const total = numOrNull(totalMassOfSample);
  const retained = numOrNull(massRetained425um);
  if (total === null || retained === null || total <= 0) return null;
  return (retained / total) * 100;
}

// USCS classifier — mirrors backend/src/calculation/uscsClassification.ts
export function aLine(ll: number): number { return 0.73 * (ll - 20); }
export function uLine(ll: number): number { return 0.9 * (ll - 8); }

export function classifyAtterberg(input: {
  ll: number | null;
  pi: number | null;
  organic?: boolean | null;
  /** FEAT-2026-0001: oven-dried retest LL — when present, drives the 75% rule. */
  retestLL?: number | null;
}): USCSGroupSymbol | null {
  const { ll, pi } = input;
  if (ll === null || pi === null) return null;
  if (Number.isNaN(ll) || Number.isNaN(pi)) return null;
  if (input.organic) {
    if (input.retestLL === null || input.retestLL === undefined) {
      return ll >= 50 ? 'OH' : 'OL';
    }
    const status = evaluateOrganicByRetest(ll, input.retestLL);
    if (status.status === 'organic' || status.status === 'incomplete') {
      return ll >= 50 ? 'OH' : 'OL';
    }
    // 'not-organic' falls through to standard classification
  }

  const aL = aLine(ll);
  if (ll < 50) {
    if (pi < 4) return 'ML';
    if (pi >= 4 && pi <= 7) return pi > aL ? 'CL-ML' : 'ML';
    return pi > aL ? 'CL' : 'ML';
  }
  return pi > aL ? 'CH' : 'MH';
}

/**
 * Apply override fields (LL/PL/PI/USCS) to computed result. Override always wins.
 * FEAT-2026-0001: USCS classification now considers retestLL when organic is on.
 */
export function applyOverrides(general: AtterbergGeneral, computed: AtterbergResult): {
  ll: number | null;
  pl: number | null;
  pi: number | null;
  uscs: string | null;
} {
  const ll = general.overrideLL ?? computed.ll;
  const pl = general.overridePL ?? computed.pl;
  const pi = general.overridePI ?? (ll !== null && pl !== null ? ll - pl : null);
  const uscs = general.overrideUSCSGroupSymbol
    ?? classifyAtterberg({
      ll, pi,
      organic: general.organic,
      retestLL: computed.retestLL,
    });
  return { ll, pl, pi, uscs };
}

function numOrNull(v: number | null | undefined): number | null {
  if (v === null || v === undefined) return null;
  if (typeof v !== 'number' || Number.isNaN(v)) return null;
  return v;
}
